import { APP_BASE_URL } from "../../config/api";
import { authFetch } from "../../utils/api";
// Driver-related admin APIs now live under /api/admin
const BASE_URL = `${APP_BASE_URL}/api/admin/safe`;

/**
 * Fetches a list of drivers associated with a specific account ID.
 * @param {number} accountId - The ID of the account.
 * @returns {Promise<Array<Object>>} A promise that resolves to an array of driver objects.
 * @throws {Error} If the fetch operation fails or the server returns an error.
 */
export async function listDriversForAccount(accountId) {
  if (!accountId) {
    console.error("🚨 Account ID is required to fetch drivers.");
    throw new Error("Account ID is required.");
  }
  console.log("listDriversForAccount>drivers call");
  const url = `${BASE_URL}/${accountId}/drivers`;
  console.log("🔍 API Call:", { method: "GET", url });
  try {
    const res = await authFetch(url);
    console.log("📥 Response Status:", res.status);

    if (!res.ok) {
      const errorText = await res.text();
      console.error("❌ Error fetching drivers:", {
        status: res.status,
        statusText: res.statusText,
        body: errorText,
        accountId,
      });
      throw new Error(
        `Failed to fetch drivers for account ${accountId}: ${res.status} ${res.statusText}`,
      );
    }

    const data = await res.json();
    console.log("✅ Success fetching drivers:", {
      accountId,
      count: data.length,
      data,
    });
    return data;
  } catch (error) {
    console.error("🚨 Request Failed (listDriversForAccount):", {
      message: error.message,
      accountId,
    });
    throw error; // Re-throw the error to be caught by the caller
  }
}

export async function listRecentDriversForAccount(accountId) {
  if (!accountId) {
    console.error("🚨 Account ID is required to fetch drivers.");
    throw new Error("Account ID is required.");
  }
  console.log("listDriversForAccount>recent_drivers call");
  const url = `${BASE_URL}/recent_drivers/${accountId}`;

  console.log("🔍 API Call:", { method: "GET", url });
  try {
    const res = await authFetch(url);
    console.log("📥 Response Status:", res.status);

    if (!res.ok) {
      const errorText = await res.text();
      console.error("❌ Error fetching drivers:", {
        status: res.status,
        statusText: res.statusText,
        body: errorText,
        accountId,
      });
      throw new Error(
        `Failed to fetch drivers for account ${accountId}: ${res.status} ${res.statusText}`,
      );
    }

    const data = await res.json();
    console.log("✅ Success fetching drivers:", {
      accountId,
      count: data.length,
      data,
    });
    return data;
  } catch (error) {
    console.error("🚨 Request Failed (listDriversForAccount):", {
      message: error.message,
      accountId,
    });
    throw error; // Re-throw the error to be caught by the caller
  }
}

/**
 * Fetches a list of completed drivers (those with scores) for a specific account ID.
 * @param {number} accountId - The ID of the account.
 * @returns {Promise<Array<Object>>} A promise that resolves to an array of completed driver objects with scores.
 * @throws {Error} If the fetch operation fails or the server returns an error.
 */
export async function listCompletedDrivers(accountId) {
  if (!accountId) {
    console.error("🚨 Account ID is required to fetch completed drivers.");
    throw new Error("Account ID is required.");
  }

  const url = `${BASE_URL}/${accountId}/completed-drivers`;
  console.log("🔍 API Call:", { method: "GET", url });

  try {
    const res = await authFetch(url);
    console.log("📥 Response Status:", res.status);

    if (!res.ok) {
      const errorText = await res.text();
      console.error("❌ Error fetching completed drivers:", {
        status: res.status,
        statusText: res.statusText,
        body: errorText,
        accountId,
      });
      throw new Error(
        `Failed to fetch completed drivers for account ${accountId}: ${res.status} ${res.statusText}`,
      );
    }

    const data = await res.json();
    console.log("✅ Success fetching completed drivers:", {
      accountId,
      count: data.length,
      data,
    });
    return data;
  } catch (error) {
    console.error("🚨 Request Failed (listCompletedDrivers):", {
      message: error.message,
      accountId,
    });
    throw error;
  }
}

export const fetchDriverMVR = async (driverId, state) => {
  if (!driverId) {
    console.error("🚨 Driver ID is required to fetch MVR.");
    throw new Error("Driver ID is required.");
  }

  if (!state) {
    console.error("🚨 State is required to fetch MVR.");
    throw new Error("State is required.");
  }

  const url = `${BASE_URL}/drivers/${driverId}/mvr?state=${state}`;
  console.log("🔍 API Call:", { method: "GET", url, state });

  try {
    const response = await authFetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
    });

    console.log("📥 Response Status:", response.status);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        detail: `HTTP error! status: ${response.status}`,
      }));
      console.error("❌ Error fetching MVR:", {
        status: response.status,
        statusText: response.statusText,
        driverId,
        state,
      });
      throw new Error(
        errorData.detail ||
          `Failed to fetch MVR for driver ${driverId} in state ${state}`,
      );
    }

    const data = await response.json();
    console.log("✅ Success fetching MVR:", { driverId, state, data });
    return data;
  } catch (error) {
    console.error("🚨 Request Failed (fetchDriverMVR):", {
      message: error.message,
      driverId,
      state,
    });
    throw error;
  }
};
